import { useEffect, useRef } from 'react';
import RouteError from '@/components/ui/route-error';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Flex } from '@/components/ui/lib/flex';
import { Spinner } from '@/components/ui/lib/spinner';
import { useBoolean } from '@/components/ui/lib/use-boolean';
import ActivationLayout from '@/features/activation/components/activation-layout';
import ActivateStep from '@/features/activation/components/activate-step';
import CodeEntryStep from '@/features/activation/components/code-entry-step';
import SuccessStep from '@/features/activation/components/success-step';
import NotFoundStep from '@/features/activation/components/not-found-step';
import { useActivationStore } from '@/features/activation/stores/activation-store';
import { validateActivationToken } from '@/features/activation/api/activation-api';
import type { ActivationStep as ActivationStepName } from '@/features/activation/types/activation';

const ActivationStep = ({ step }: { step: ActivationStepName }) => {
  switch (step) {
    case 'activate':
      return <ActivateStep />;
    case 'code-entry':
      return <CodeEntryStep />;
    case 'success':
      return <SuccessStep />;
    default:
      return <NotFoundStep />;
  }
};

const ActivatePage = () => {
  const { token: tokenParam } = Route.useSearch();
  const { currentStep, userName, token, setToken, setUserInfo, setStep } = useActivationStore();
  const validatedRef = useRef(false);
  const [isValidating, setIsValidating] = useBoolean(false);

  // Intentional deviation from Rule 1: this is a one-shot mount side-effect (peeking the token from
  // the activation link), not cached server state — there is nothing to invalidate or refetch, so a
  // plain fetcher call is simpler than a query hook (mirrors useBootstrapAuth).
  //
  // It is a *peek*, never a redemption: corporate mail scanners follow links before a human does,
  // and a page that spent the credential on arrival would hand the recipient a dead one. The
  // credential is only ever spent by the explicit click in ActivateStep.
  useEffect(() => {
    // One-shot for every path through it: once the entry step has been chosen, a later re-render
    // must not reset it — the flow moves on to 'success' from inside the steps below.
    if (validatedRef.current) return;

    const effectiveToken = tokenParam ?? token;

    // No token anywhere — the reader is on a different device from their mail, or came here
    // directly. Fall back to typing the address and the emailed code.
    if (!effectiveToken) {
      validatedRef.current = true;
      setStep('code-entry');
      return;
    }

    if (tokenParam && tokenParam !== token) {
      setToken(tokenParam);
    }

    validatedRef.current = true;
    setIsValidating.on();

    validateActivationToken(effectiveToken)
      .then((response) => {
        if (!response.isValid) {
          setStep('not-found');
          return;
        }
        setUserInfo(response.email ?? '', response.name ?? '');
        setStep('activate');
      })
      .catch(() => {
        setStep('not-found');
      })
      .finally(() => {
        setIsValidating.off();
      });
  }, [tokenParam, token, setToken, setUserInfo, setStep, setIsValidating]);

  if (isValidating) {
    return (
      <ActivationLayout currentStep={currentStep} userName={userName}>
        <Flex justify="center" py="12">
          <Spinner />
        </Flex>
      </ActivationLayout>
    );
  }

  return (
    <ActivationLayout currentStep={currentStep} userName={userName}>
      <ActivationStep step={currentStep} />
    </ActivationLayout>
  );
};

export const Route = createFileRoute('/activate')({
  validateSearch: z.object({
    token: z.string().optional(),
  }),
  component: ActivatePage,
  errorComponent: RouteError,
});
